import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { Button } from '../../../common/Button';
import { closeStarsModal } from '../actions/ide';
import { injectConstellations } from '../actions/constellations';
import { CONSTELLATION_NAMES } from '../../../utils/constellationCatalog';
import { filenameFor } from '../../../utils/constellationInject';

/**
 * Modal to pick which constellations are injected into the current sketch. Each
 * constellation maps to a `<slug>.js` data file; the checkbox reflects whether that
 * file is currently in the sketch, and Apply writes the add/remove diff.
 */
const StarsModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const files = useSelector((state) => state.files);

  const presentNames = CONSTELLATION_NAMES.filter((name) =>
    files.some((f) => f.fileType === 'file' && f.name === filenameFor(name))
  );
  const [selected, setSelected] = useState(() => new Set(presentNames));

  const toggle = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const apply = () => {
    dispatch(injectConstellations([...selected]));
    dispatch(closeStarsModal());
  };

  return (
    <Modal
      title={t('StarsModal.Title', { defaultValue: 'Add constellations' })}
      closeAriaLabel={t('StarsModal.CloseButtonARIA', {
        defaultValue: 'Close constellation picker'
      })}
      onClose={() => dispatch(closeStarsModal())}
      contentClassName="stars-modal"
    >
      <p className="stars-modal__intro">
        {t('StarsModal.Intro', {
          defaultValue:
            'Each constellation is added as a data file that registers into ' +
            'window.constellations (RA/Dec in degrees). Access it in your sketch, ' +
            'e.g. constellations["Orion"]["Betelgeuse"].'
        })}
      </p>
      <ul className="stars-modal__list">
        {CONSTELLATION_NAMES.map((name) => (
          <li key={name} className="stars-modal__item">
            <label htmlFor={`constellation-${name}`}>
              <input
                id={`constellation-${name}`}
                type="checkbox"
                checked={selected.has(name)}
                onChange={() => toggle(name)}
              />
              {name}
            </label>
          </li>
        ))}
      </ul>
      <div className="stars-modal__actions">
        <Button onClick={() => dispatch(closeStarsModal())}>
          {t('Common.Cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button onClick={apply}>
          {t('StarsModal.Apply', { defaultValue: 'Apply' })}
        </Button>
      </div>
    </Modal>
  );
};

export default StarsModal;
